import { useKnowledgeStore, type KnowledgeTab } from '@/stores/knowledge';
import { CanonicalGraph } from './CanonicalGraph';
import { OntologyPanel, OntologicalClassBadge } from './OntologyPanel';
import { ResolutionHistory } from './ResolutionHistory';
import { SourceLineage } from './SourceLineage';
import { CatalogMembershipsTab } from './CatalogMembershipsTab';

// Color map for semantic relation groups (mirrors CanonicalGraph.tsx)
const RELATION_COLORS: Record<string, string> = {
  subset_of: '#60a5fa',          broader_than: '#60a5fa',
  complements: '#34d399',        co_activates_with: '#34d399',
  competes_with: '#f87171',      contrasts_with: '#f87171',
  amplifies: '#fb923c',          enables: '#fb923c',          precondition_for: '#fb923c',
  precedes_in_journey: '#a78bfa',escalates_to: '#a78bfa',    triggers: '#a78bfa',
  substitutes_for: '#f472b6',    variant_of: '#f472b6',       concurrent_with: '#f472b6',
  activates: '#facc15',          seeks_fulfillment_from: '#facc15',
  signals_intent_for: '#facc15', drives_consideration_in: '#facc15',
  creates_barrier_against: '#f97316', inhibits_consideration_in: '#f97316',
};

function relColor(type: string) {
  return RELATION_COLORS[type] ?? '#94a3b8';
}

function fmtRel(type: string) {
  return type.replace(/_/g, ' ');
}

const TABS: { id: KnowledgeTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'ontology', label: 'Ontology' },
  { id: 'catalogs', label: 'Catalog Memberships' },
  { id: 'graph', label: 'Graph' },
  { id: 'history', label: 'History' },
  { id: 'sources', label: 'Sources' },
];

export function CanonicalEntityDetail() {
  const { selectedCanonical, activeTab, setActiveTab, detailLoading } = useKnowledgeStore();

  if (!selectedCanonical) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        Select a canonical entity from the list
      </div>
    );
  }

  if (detailLoading) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        Loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 bg-white">
        <h2 className="text-lg font-semibold text-navy-900 truncate">
          {selectedCanonical.canonical_name}
        </h2>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
            {selectedCanonical.entity_type}
          </span>
          {selectedCanonical.ontological_class && selectedCanonical.ontological_class !== 'uncategorized' && (
            <OntologicalClassBadge
              ontologicalClass={selectedCanonical.ontological_class}
              confidence={selectedCanonical.ontological_classification_confidence}
            />
          )}
          <span className="text-sm text-slate-400">v{selectedCanonical.version}</span>
          <span className="text-sm text-slate-400">{selectedCanonical.source_entity_count} sources</span>
          <span className="text-sm text-emerald-600 font-medium">
            {(selectedCanonical.confidence * 100).toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-white px-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-brand-teal border-b-2 border-brand-teal'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 bg-slate-50">
        {activeTab === 'overview' && <OverviewPanel />}
        {activeTab === 'ontology' && <OntologyPanel />}
        {activeTab === 'catalogs' && (
          <CatalogMembershipsTab canonical={selectedCanonical} />
        )}
        {activeTab === 'graph' && <CanonicalGraph />}
        {activeTab === 'history' && <ResolutionHistory />}
        {activeTab === 'sources' && <SourceLineage />}
      </div>
    </div>
  );
}

function OverviewPanel() {
  const { selectedCanonical, subgraph } = useKnowledgeStore();
  if (!selectedCanonical) return null;

  const data = selectedCanonical.canonical_data || {};
  const entries = Object.entries(data);

  // Pull edges from the subgraph that touch this entity
  const edges = subgraph?.edges ?? [];
  const myId = selectedCanonical.id;
  const nodeMap = Object.fromEntries((subgraph?.nodes ?? []).map((n) => [n.id, n]));

  const outEdges = edges.filter((e) => e.source === myId);
  const inEdges = edges.filter((e) => e.target === myId && e.source !== myId);

  return (
    <div className="space-y-4">
      {/* Quality Metrics */}
      <div className="grid grid-cols-3 gap-3">
        <MetricCard label="Confidence" value={`${(selectedCanonical.confidence * 100).toFixed(1)}%`} />
        <MetricCard label="Completeness" value={`${(selectedCanonical.completeness * 100).toFixed(1)}%`} />
        <MetricCard label="Data Quality" value={`${(selectedCanonical.data_quality_score * 100).toFixed(1)}%`} />
      </div>

      {/* Semantic Relations */}
      {(outEdges.length > 0 || inEdges.length > 0) && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            Semantic Relations
            <span className="ml-2 text-xs text-slate-400 font-normal">({outEdges.length + inEdges.length} edges)</span>
          </h3>
          <div className="space-y-1.5">
            {outEdges.map((e, i) => {
              const peer = nodeMap[e.target];
              return (
                <div key={`out-${i}`} className="flex items-center gap-2 text-xs">
                  <span className="text-slate-400 shrink-0">this</span>
                  <span
                    className="px-1.5 py-0.5 rounded text-white font-medium shrink-0"
                    style={{ backgroundColor: relColor(e.relation_type), fontSize: '10px' }}
                  >
                    {fmtRel(e.relation_type)}
                  </span>
                  <span className="text-slate-700 truncate">{peer?.name ?? e.target.slice(0, 8)}</span>
                  <span className="text-slate-400 shrink-0 ml-auto">{peer?.entity_type ?? ''}</span>
                </div>
              );
            })}
            {inEdges.map((e, i) => {
              const peer = nodeMap[e.source];
              return (
                <div key={`in-${i}`} className="flex items-center gap-2 text-xs">
                  <span className="text-slate-700 truncate shrink-0 max-w-[100px]">{peer?.name ?? e.source.slice(0, 8)}</span>
                  <span
                    className="px-1.5 py-0.5 rounded text-white font-medium shrink-0"
                    style={{ backgroundColor: relColor(e.relation_type), fontSize: '10px' }}
                  >
                    {fmtRel(e.relation_type)}
                  </span>
                  <span className="text-slate-400 shrink-0">this</span>
                  <span className="text-slate-400 shrink-0 ml-auto">{peer?.entity_type ?? ''}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Metadata</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div className="text-slate-400">ID</div>
          <div className="text-slate-600 font-mono text-xs truncate">{selectedCanonical.id}</div>
          <div className="text-slate-400">Entity Type</div>
          <div className="text-slate-700">{selectedCanonical.entity_type}</div>
          <div className="text-slate-400">Version</div>
          <div className="text-slate-700">{selectedCanonical.version}</div>
          <div className="text-slate-400">Merge Strategy</div>
          <div className="text-slate-700">{selectedCanonical.merge_strategy || 'N/A'}</div>
          <div className="text-slate-400">Created</div>
          <div className="text-slate-700">{new Date(selectedCanonical.created_at).toLocaleString()}</div>
          <div className="text-slate-400">Updated</div>
          <div className="text-slate-700">{new Date(selectedCanonical.updated_at).toLocaleString()}</div>
        </div>
      </div>

      {/* Canonical Data */}
      {entries.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Canonical Data</h3>
          <div className="space-y-1.5 text-sm">
            {entries.map(([key, value]) => (
              <div key={key} className="flex gap-2">
                <span className="text-slate-400 min-w-[120px]">{key}</span>
                <span className="text-slate-700 truncate">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center shadow-soft">
      <div className="text-xs text-slate-400 mb-1 font-medium uppercase tracking-wide">{label}</div>
      <div className="text-lg font-bold text-navy-900">{value}</div>
    </div>
  );
}
